import { dashboardApi } from '@/store/api/base/api.base';
import { writeChatArchive, writeChatMute } from '@/store/api/conversation/chat/chat.cache';
import type { TypePageInfo } from '@/types/api/types.api';
import type { TypeChatChannel, TypeChatMember } from '@/types/conversation/types.conversation.chat.channel';
import type {
    TypeChatMessage,
    TypeChatRead,
    TypeChatReceipts,
    TypeChatReferenceInput,
} from '@/types/conversation/types.conversation.chat.message';

type TypeChatChannels = { channels: TypeChatChannel[]; members: TypeChatMember[] };

type TypeChatMessagePage = { items: TypeChatMessage[]; page: TypePageInfo };

const PAGE_SIZE = 50;

export const chatApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listChatChannels: build.query<TypeChatChannels, void>({
            query: () => ({ url: '/conversations/chat/channels' }),
        }),

        listChatMessages: build.infiniteQuery<TypeChatMessagePage, string, string | null>({
            infiniteQueryOptions: {
                initialPageParam: null,
                getNextPageParam: (last) => (last.page.hasMore && last.page.nextCursor ? last.page.nextCursor : undefined),
            },
            query: ({ queryArg, pageParam }) => ({
                url: `/conversations/chat/channels/${encodeURIComponent(queryArg)}/messages`,
                params: { limit: PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) },
            }),
            keepUnusedDataFor: 300,
        }),

        sendChatMessage: build.mutation<TypeChatMessage, { channelId: string; text: string; mentions: string[]; replyToId: string | null; files: number; clientRef: string; ephemeral: number; references: TypeChatReferenceInput[] }>({
            query: ({ channelId, text, mentions, replyToId, files, clientRef, ephemeral, references }) => ({
                url: `/conversations/chat/channels/${encodeURIComponent(channelId)}/messages`,
                method: 'post',
                data: { text, mentions, replyToId: replyToId ?? '', files, clientRef, ephemeral, references },
            }),
        }),

        editChatMessage: build.mutation<TypeChatMessage, { id: string; text: string; mentions: string[]; references: TypeChatReferenceInput[] }>({
            query: ({ id, text, mentions, references }) => ({ url: `/conversations/chat/messages/${encodeURIComponent(id)}`, method: 'patch', data: { text, mentions, references } }),
        }),

        removeChatMessage: build.mutation<{ id: string; channelId: string }, string>({
            query: (id) => ({ url: `/conversations/chat/messages/${encodeURIComponent(id)}`, method: 'delete' }),
        }),

        settleChatFiles: build.mutation<TypeChatMessage, { id: string; drop: number }>({
            query: ({ id, drop }) => ({ url: `/conversations/chat/messages/${encodeURIComponent(id)}/files/settle`, method: 'post', data: { drop } }),
        }),

        markChatRead: build.mutation<{ channelId: string; lastReadAt: string }, { channelId: string; messageId: string }>({
            query: ({ channelId, messageId }) => ({ url: `/conversations/chat/channels/${encodeURIComponent(channelId)}/read`, method: 'post', data: { messageId } }),
        }),

        archiveChatChannel: build.mutation<{ channelId: string; archivedAt: string | null }, { channelId: string; archived: boolean }>({
            query: ({ channelId, archived }) => ({ url: `/conversations/chat/channels/${encodeURIComponent(channelId)}/archive`, method: 'post', data: { archived } }),
            onQueryStarted: async ({ channelId, archived }, { dispatch, queryFulfilled }) => {
                const patch = dispatch(chatApi.util.updateQueryData('listChatChannels', undefined, (draft) => {
                    const channel = draft.channels.find((entry) => entry.id === channelId);
                    if (channel) channel.archivedAt = archived ? new Date().toISOString() : null;
                }));
                try {
                    const { data } = await queryFulfilled;
                    writeChatArchive(dispatch, data);
                } catch {
                    patch.undo();
                }
            },
        }),

        muteChatChannel: build.mutation<{ channelId: string; mutedUntil: string | null }, { channelId: string; seconds: number }>({
            query: ({ channelId, seconds }) => ({ url: `/conversations/chat/channels/${encodeURIComponent(channelId)}/mute`, method: 'post', data: { seconds } }),
            onQueryStarted: async ({ channelId, seconds }, { dispatch, queryFulfilled }) => {
                const patch = dispatch(chatApi.util.updateQueryData('listChatChannels', undefined, (draft) => {
                    const channel = draft.channels.find((entry) => entry.id === channelId);
                    if (channel) channel.mutedUntil = seconds === 0 ? null : new Date(seconds < 0 ? Date.UTC(9999, 11, 31) : Date.now() + seconds * 1000).toISOString();
                }));
                try {
                    const { data } = await queryFulfilled;
                    writeChatMute(dispatch, data);
                } catch {
                    patch.undo();
                }
            },
        }),

        listChatReads: build.query<TypeChatRead[], string>({
            query: (channelId) => ({ url: `/conversations/chat/channels/${encodeURIComponent(channelId)}/reads` }),
            keepUnusedDataFor: 300,
        }),

        readChatReceipts: build.query<TypeChatReceipts, { id: string; channelId: string }>({
            query: ({ id }) => ({ url: `/conversations/chat/messages/${encodeURIComponent(id)}/receipts` }),
            providesTags: (_result, _error, { channelId }) => [{ type: 'ChatReceipts', id: channelId }],
            keepUnusedDataFor: 30,
        }),
    }),
});

export const {
    useListChatChannelsQuery, useListChatMessagesInfiniteQuery, useSendChatMessageMutation, useEditChatMessageMutation,
    useRemoveChatMessageMutation, useSettleChatFilesMutation, useMarkChatReadMutation, useReadChatReceiptsQuery, useArchiveChatChannelMutation, useListChatReadsQuery,
    useMuteChatChannelMutation,
} = chatApi;

export default chatApi;
